/**
 * /ninety/meetings/:id — the live Level-10 run view (F58 Phase 3, reworked per
 * owner feedback).
 *
 * A live meeting takes over the FULL SCREEN (fixed overlay above the app
 * shell) and runs as a STAGED flow — one agenda stage at a time, in the
 * template order (migration 0134): Opening → Measurables → To-Dos →
 * Issues (IDS) → Rocks → Rate & Conclude. A stepper across the top shows
 * every stage (done / current / upcoming) and any attendee can click a stage
 * to jump (server 'goto'), press Next to advance, or Conclude on the last
 * stage. The Measurables stage shows the SELECTED TEAM's scorecard
 * (TeamMeasurables), so the numbers are up for review during the meeting.
 *
 * Scheduled meetings auto-start on arrival (every meeting here is created by
 * "Start Level-10" — there is no future-scheduling UI), so clicking Start
 * drops the team straight into the full-screen Opening stage. Concluded
 * meetings render as a normal summary page.
 *
 * Multi-attendee sync is via Supabase Realtime (useMeetingBundle subscribes to
 * postgres_changes); a 20s background poll is the graceful fallback.
 */

import { useEffect, useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, StatusPill } from '~/components/ui';
import { useAuth } from '~/lib/auth';
import {
  startMeeting, advanceSegment, gotoSegment, concludeMeeting, cancelMeeting,
  type SegmentKey,
} from '~/lib/api/meetings';
import { useMeetingBundle } from '~/components/ninety/meeting/useMeetingData';
import { SegmentTimer } from '~/components/ninety/meeting/SegmentTimer';
import { AttendancePanel } from '~/components/ninety/meeting/AttendancePanel';
import { IdsList } from '~/components/ninety/meeting/IdsList';
import { RockReviewPanel } from '~/components/ninety/meeting/RockReviewPanel';
import { MeetingTodosPanel } from '~/components/ninety/meeting/MeetingTodosPanel';
import { HeadlinesPanel } from '~/components/ninety/meeting/HeadlinesPanel';
import { TeamMeasurables } from '~/components/ninety/meeting/TeamMeasurables';

export const Route = createFileRoute('/_app/ninety/meetings/$id')({
  component: MeetingRunPage,
});

function MeetingRunPage() {
  const { id } = Route.useParams();
  const auth = useAuth();
  const uid = auth.user?.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, error } = useMeetingBundle(id);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['meeting', id] });
  const start = useMutation({ mutationFn: () => startMeeting(id), onSuccess: invalidate });
  const advance = useMutation({ mutationFn: () => advanceSegment(id), onSuccess: invalidate });
  const goto = useMutation({ mutationFn: (k: SegmentKey) => gotoSegment(id, k), onSuccess: invalidate });
  const conclude = useMutation({ mutationFn: () => concludeMeeting(id), onSuccess: invalidate });
  const cancel = useMutation({
    mutationFn: () => cancelMeeting(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
      navigate({ to: '/ninety/meetings' });
    },
  });

  const meeting = data?.meeting ?? null;

  // Auto-start: every meeting is created via "Start Level-10", so a scheduled
  // meeting you navigate into should go live immediately — no second Start
  // button interstitial (the old flow read as "it didn't load a meeting").
  const shouldAutoStart = meeting?.status === 'scheduled' && !start.isPending && !start.isError;
  useEffect(() => {
    if (shouldAutoStart) start.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoStart]);

  const nameByUser = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of data?.roster ?? []) m[p.user_id] = p.display_name;
    return m;
  }, [data]);

  if (isLoading) return <div className="px-6 py-12 text-sm text-[var(--color-ink-muted)]">Loading meeting…</div>;
  if (error || !data || !meeting) {
    return (
      <div className="px-6 py-12 max-w-2xl">
        <h1 className="font-display text-2xl font-bold text-[var(--color-danger)]">Couldn't load this meeting</h1>
        <p className="text-sm text-[var(--color-ink-soft)] mt-2">{(error as Error | undefined)?.message ?? 'Not found.'}</p>
        <Link to="/ninety/meetings" className="text-sm text-[var(--color-link)] mt-3 inline-block">&larr; Back to Meetings</Link>
      </div>
    );
  }

  // ── Concluded → normal summary page (no full-screen takeover). ──
  if (meeting.status === 'concluded') {
    return (
      <div className="px-4 sm:px-6 py-6 space-y-4 max-w-5xl">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/ninety/meetings" className="text-sm text-[var(--color-link)]">&larr; Meetings</Link>
          <h1 className="font-display text-2xl font-bold text-[var(--color-navy)] flex-1">
            {meeting.title ?? 'Level-10 Meeting'}{data.teamName ? ` · ${data.teamName}` : ''}
          </h1>
          <StatusPill kind="completed">Concluded</StatusPill>
        </div>
        <Card>
          <CardBody className="text-sm text-[var(--color-ink-soft)]">
            Meeting concluded.
            {typeof meeting.rating_avg === 'number' && <> Average rating <strong>{meeting.rating_avg}/10</strong>.</>}
            {' '}Open issues were rolled into the owners' to-dos.
          </CardBody>
        </Card>
        <IdsList meetingId={id} issues={data.issues} nameByUser={nameByUser} readOnly />
        {data.headlines.length > 0 && (
          <HeadlinesPanel meetingId={id} headlines={data.headlines} readOnly />
        )}
        <AttendancePanel
          meetingId={id} roster={data.roster} attendance={data.attendance}
          currentUserId={uid} showRating
        />
      </div>
    );
  }

  // ── Scheduled (auto-start in flight) / Live → full-screen staged takeover. ──
  const currentKey = meeting.current_segment_key as SegmentKey | null;
  const idx = data.segments.findIndex((s) => s.segment_key === currentKey);
  const isLast = idx >= 0 && idx === data.segments.length - 1;
  const starting = meeting.status === 'scheduled';

  return (
    // Takes over the screen EXCEPT the left menu bar (owner request): offset by
    // the 224px sidebar on desktop; full-bleed on mobile where the sidebar is
    // an off-canvas drawer (globals.css @767px).
    <div className="fixed inset-y-0 left-0 right-0 md:left-[224px] z-[70] flex flex-col overflow-y-auto bg-[#eef2f7]">
      {/* Header band — title, team, cancel/exit; stepper beneath. Full width
          (owner: the meeting should fit the whole screen beside the menu). */}
      <div className="sticky top-0 z-10 border-b border-[var(--color-line)] bg-white shadow-sm">
        <div className="w-full px-4 sm:px-6 pt-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="font-display text-xl font-bold text-[var(--color-navy)]">
              {meeting.title ?? 'Level-10 Meeting'}
            </h1>
            {data.teamName && (
              <span className="rounded-full bg-[var(--color-navy)] px-2.5 py-0.5 text-xs font-bold text-white">{data.teamName}</span>
            )}
            <StatusPill kind={starting ? 'pending' : 'info'}>{starting ? 'Starting…' : 'Live'}</StatusPill>
            <span className="ml-auto flex items-center gap-2">
              <button
                type="button"
                disabled={cancel.isPending}
                onClick={() => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm('Cancel this meeting? It will be deleted — team issues raised in it stay on the team\'s IDS list.')) {
                    cancel.mutate();
                  }
                }}
                className="rounded-md border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {cancel.isPending ? 'Cancelling…' : 'Cancel meeting'}
              </button>
              <Link
                to="/ninety/meetings"
                className="rounded-md border border-[var(--color-line)] px-3 py-1.5 text-sm font-semibold text-[var(--color-ink-soft)] hover:bg-slate-50"
              >
                ✕ Exit
              </Link>
            </span>
          </div>
          {cancel.isError && (
            <div className="mt-1 text-xs font-medium text-[var(--color-danger,#dc2626)]">
              {(cancel.error as Error).message}
            </div>
          )}

          {/* Stage stepper — every agenda step, click to jump. */}
          <nav className="mt-2 flex gap-1 overflow-x-auto pb-2" aria-label="Meeting stages">
            {data.segments.map((s, i) => {
              const state = i < idx ? 'done' : i === idx ? 'current' : 'todo';
              return (
                <button
                  key={s.segment_key}
                  type="button"
                  disabled={starting || goto.isPending || state === 'current'}
                  onClick={() => goto.mutate(s.segment_key)}
                  className={
                    'flex shrink-0 items-center gap-1.5 rounded-t-md px-3 py-1.5 text-[13px] font-semibold transition-colors ' +
                    (state === 'current'
                      ? 'bg-[var(--color-navy)] text-white'
                      : state === 'done'
                        ? 'text-emerald-700 hover:bg-emerald-50'
                        : 'text-[var(--color-ink-muted)] hover:bg-slate-100')
                  }
                  title={`${s.label} · ${s.duration_min} min`}
                >
                  <span
                    className={
                      'inline-flex h-4.5 w-4.5 items-center justify-center rounded-full text-[10px] font-bold ' +
                      (state === 'current' ? 'bg-white/25 text-white'
                        : state === 'done' ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-200 text-slate-600')
                    }
                    style={{ width: 18, height: 18 }}
                  >
                    {state === 'done' ? '✓' : i + 1}
                  </span>
                  {s.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Body — timer + the CURRENT stage only, stretched to the full width. */}
      <div className="w-full flex-1 space-y-4 px-4 sm:px-6 py-4">
        {starting ? (
          <Card>
            <CardBody className="py-10 text-center text-sm text-[var(--color-ink-muted)]">
              {start.isError
                ? <span className="text-[var(--color-danger)]">Could not start: {(start.error as Error).message}</span>
                : 'Starting the meeting…'}
            </CardBody>
          </Card>
        ) : (
          <>
            <SegmentTimer
              segments={data.segments}
              currentKey={currentKey}
              startedAt={meeting.current_segment_started_at}
              onAdvance={() => advance.mutate()}
              onConclude={() => conclude.mutate()}
              advancing={advance.isPending || conclude.isPending}
              canControl
            />
            <StageContent
              stage={currentKey}
              meetingId={id}
              data={data}
              nameByUser={nameByUser}
              currentUserId={uid}
              isLast={isLast}
            />
          </>
        )}
      </div>
    </div>
  );
}

/** The one panel for the current stage — the staged flow shows each step alone. */
function StageContent({ stage, meetingId, data, nameByUser, currentUserId, isLast }: {
  stage: SegmentKey | null;
  meetingId: string;
  data: NonNullable<ReturnType<typeof useMeetingBundle>['data']>;
  nameByUser: Record<string, string>;
  currentUserId: string | undefined;
  isLast: boolean;
}) {
  switch (stage) {
    case 'segue':
      return (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardBody className="space-y-2">
              <h3 className="font-bold text-[var(--color-navy)]">Opening</h3>
              <p className="text-sm text-[var(--color-ink-soft)]">
                Go around the room — everyone shares in under a minute:
              </p>
              <ul className="list-disc pl-5 text-sm text-[var(--color-ink-soft)] space-y-1">
                <li>One piece of <strong>good news</strong> (personal or business).</li>
                <li>Your <strong>best thing since last week</strong> for the team.</li>
              </ul>
              <p className="text-xs text-[var(--color-ink-muted)]">
                Check everyone in on the right, then hit <strong>Next segment</strong> to review the numbers.
              </p>
            </CardBody>
          </Card>
          <AttendancePanel
            meetingId={meetingId} roster={data.roster} attendance={data.attendance}
            currentUserId={currentUserId} showRating={false}
          />
        </div>
      );
    case 'data':
      return <TeamMeasurables teamName={data.teamName} />;
    case 'todo':
      return <MeetingTodosPanel />;
    case 'ids':
      return <IdsList meetingId={meetingId} issues={data.issues} nameByUser={nameByUser} readOnly={false} />;
    case 'rock_review':
      return <RockReviewPanel roster={data.roster} />;
    case 'headlines': // legacy template stage (removed in 0134) — still renders if hit
      return <HeadlinesPanel meetingId={meetingId} headlines={data.headlines} readOnly={false} />;
    case 'conclude':
      return (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardBody className="space-y-2">
              <h3 className="font-bold text-[var(--color-navy)]">Rate &amp; Conclude</h3>
              <p className="text-sm text-[var(--color-ink-soft)]">
                Everyone rates the meeting 1–10 on the right. Recap: unsolved issues carry to next week;
                each solved issue's follow-up rolls into the owner's to-dos when you hit{' '}
                <strong>Conclude</strong>{isLast ? ' on the timer above' : ''}.
              </p>
            </CardBody>
          </Card>
          <AttendancePanel
            meetingId={meetingId} roster={data.roster} attendance={data.attendance}
            currentUserId={currentUserId} showRating
          />
        </div>
      );
    default:
      return (
        <Card>
          <CardBody className="py-8 text-center text-sm text-[var(--color-ink-muted)]">
            Waiting for the first stage…
          </CardBody>
        </Card>
      );
  }
}
